"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuTrigger, NavigationMenuContent, NavigationMenuLink } from "@/components/ui/navigation-menu";
import { MenuProvider } from "@/integrations/wordpress/WordPressMenuProvider";
const sections = [{
  name: "Malwarebytes",
  id: "/MalwarebytesPage"
}, {
  name: "NordPass",
  id: "#nordpasspromo"
}, {
  name: "Incogni",
  id: "#incognipromo"
}, {
  name: "F-Secure",
  id: "#fsecurepromo"
}, {
  name: "McAfee",
  id: "#mcafeepromo"
}, {
  name: "Troop Messenger",
  id: "#troopmessengerpromo"
}, {
  name: "Dashlane",
  id: "#dashlanepromo"
}, {
  name: "Blog",
  id: "#blog"
}, {
  name: "Contact",
  id: "#contact"
}];
export default function Hero() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const tickerWords = ["SECURE", "PRIVATE", "ANONYMOUS", "GLOBAL", "ENCRYPTED", "SECURE", "PRIVATE", "ANONYMOUS", "GLOBAL", "ENCRYPTED"];
  return <section data-section-id="1270" id="hero" className="relative bg-background text-foreground min-h-screen w-full overflow-hidden">
      <div aria-hidden="true" className="hidden md:block absolute inset-0 pointer-events-none" style={{
      background: "var(--primary)",
      clipPath: "polygon(60% 100%, 100% 100%, 100% 35%)"
    }} />

      <div aria-hidden="true" className="block md:hidden absolute inset-x-0 bottom-0 h-32 pointer-events-none" style={{
      background: "var(--primary)",
      clipPath: "polygon(0 60%, 100% 30%, 100% 100%, 0 100%)"
    }} />

      <div className="absolute top-6 md:top-10 left-4 md:left-12 z-20 flex items-center gap-6">
        <div className="flex items-center gap-3">
          <span aria-hidden="true" className="inline-block h-px" style={{
          width: "24px",
          background: "var(--primary)"
        }} />
          <span className="font-mono text-[0.875rem] uppercase" style={{
          letterSpacing: "0.15em",
          color: "var(--muted-foreground)"
        }}>
            PROTOCOL 01 
          </span>
        </div>

        <MenuProvider menu_id="167">
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger className="bg-transparent hover:bg-transparent data-[state=open]:bg-transparent">
                  MENU
                </NavigationMenuTrigger>
                <NavigationMenuContent className="min-w-[200px] p-2">
                  {sections.map(section => <NavigationMenuLink key={section.id} asChild>
                      <Link to={section.id} className="block px-4 py-2 hover:bg-accent rounded-sm">
                        {section.name}
                      </Link>
                    </NavigationMenuLink>)}
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </MenuProvider>
      </div>

      <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8 md:px-12 pt-28 md:pt-32 pb-40 md:pb-32 min-h-screen flex items-center">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 w-full items-center">
          <div ref={leftRef} className="order-2 md:order-1 md:col-span-7 transition-all duration-700 ease-out" style={{
          transform: leftVisible ? "translateY(0)" : "translateY(24px)",
          opacity: leftVisible ? 1 : 0
        }}>
            <h1 className="font-default uppercase text-foreground" style={{
            fontSize: "clamp(3.5rem, 9vw, 8.5rem)",
            lineHeight: "1.1",
            letterSpacing: "-0.04em",
            fontWeight: 800
          }}>
              DIGITAL{" "}
              <em className="not-italic md:italic inline-block" style={{
              color: "var(--primary)",
              fontStyle: "italic"
            }}>
                SOVEREIGNTY
              </em>{" "}
              COMMAND.
            </h1>

            <p className="mt-8 md:mt-10 max-w-xl font-default text-foreground/80" style={{
            fontSize: "clamp(1.125rem, 1.2vw, 1.25rem)",
            lineHeight: "1.6"
          }}>
              VPNGENIX provides military-grade encryption and global server access to ensure your digital footprint remains invisible.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4 sm:gap-5">
              <Link to="https://vpngenix.com/go/Nordpass" newTab>
                <Button className="h-14 px-8 bg-primary text-white hover:bg-orange-600 uppercase font-default rounded-sm w-full sm:w-auto" style={{
                fontSize: "1rem",
                letterSpacing: "0.05em"
              }}>
                  SECURE CONNECTION
                </Button>
              </Link>

              <Link to="/CybersecurityCyberGenixPage">
                <Button variant="outline" className="h-14 px-8 bg-transparent border-2 border-foreground text-foreground hover:bg-primary hover:text-white hover:border-primary uppercase font-default rounded-sm w-full sm:w-auto" style={{
                fontSize: "1rem",
                letterSpacing: "0.05em"
              }}>
                  EXPLORE PROTOCOLS
                </Button>
              </Link>
            </div>
          </div>

          <div ref={rightRef} className="order-1 md:order-2 md:col-span-5 relative transition-all duration-700 ease-out" style={{
          transform: rightVisible ? "translateY(0)" : "translateY(24px)",
          opacity: rightVisible ? 1 : 0
        }}>
            <div className="relative w-full aspect-[3/4] md:aspect-[4/5] overflow-hidden rounded-sm">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/cyber_security_shield.png" alt="High-contrast digital shield representing secure VPN encryption" loading="lazy" decoding="async" className="w-full h-full object-cover" style={{
              filter: "grayscale(0.6) contrast(1.1)"
            }} />

              <div className="absolute top-3 right-3 font-mono uppercase" style={{
              fontSize: "0.875rem",
              letterSpacing: "0.15em",
              color: "var(--foreground)",
              background: "hsla(0, 0%, 4%, 0.6)",
              padding: "4px 8px"
            }}>
                ENCRYPTED / ACTIVE
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 z-20 overflow-hidden border-t border-b" style={{
      borderColor: "var(--border)",
      background: "var(--background)"
    }}>
        <div className="flex whitespace-nowrap py-4 md:py-5" style={{
        animation: "tickerScroll 40s linear infinite"
      }}>
          {[...tickerWords, ...tickerWords].map((word, i) => <div key={i} data-index={i} className="flex items-center font-default uppercase text-foreground shrink-0" style={{
          fontSize: "clamp(1.25rem, 2vw, 1.75rem)",
          letterSpacing: "0.05em",
          fontWeight: 600
        }}>
              <span className="px-6 md:px-10">{word}</span>
              <span aria-hidden="true" className="inline-block" style={{
            width: "6px",
            height: "6px",
            borderRadius: "3px",
            background: "var(--primary)"
          }} />
            </div>)}
        </div>
      </div>

      <style>{`
        @keyframes tickerScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>;
}